import { ActivityType, CurrencyType, DepositActionType, DepositMethodType, NowTime, WithdrawWayType } from "@golden/gdk-base";
import { addDays, addHours, addMonths, addWeeks, addYears, endOfDay, endOfMonth, endOfWeek, endOfYesterday, getTime, getUnixTime, isAfter, isBefore, isEqual, isLastDayOfMonth, startOfDay, startOfMonth, startOfWeek, startOfYear, startOfYesterday, subDays, subMonths, subWeeks, subYears } from "date-fns";
import { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { BcMath } from "@golden/bcmath";
import html2canvas from "html2canvas";
import { Subject } from "rxjs";
//#region src/utils/BankImage.ts
const getBankImage = (bank) => {
	return new URL(`./assets/banks/${bank.toUpperCase()}.png`, import.meta.url).href;
};
const getBankIcon = (bank) => {
	return new URL(`./assets/bankIcons/${bank.toUpperCase()}.png`, import.meta.url).href;
};
//#endregion
//#region src/utils/TimeHelper.ts
const ONE_HOUR_IN_MS = 36e5;
const ONE_DAY_IN_HOURS = 24;
const THREE_HOURS_IN_MS = 3 * ONE_HOUR_IN_MS;
const ONE_DAY_IN_MS = ONE_DAY_IN_HOURS * ONE_HOUR_IN_MS;
const THREE_DAYS_IN_MS = 72 * ONE_HOUR_IN_MS;
const PERMANENT_TIME = 0xe677d0677da0;
function getSubFnBySubType(timeType) {
	if (timeType === "month") return subMonths;
	if (timeType === "year") return subYears;
	if (timeType === "day") return subDays;
	throw new Error("timeType must be month, year or day. Got " + String(timeType));
}
function getAddFnBySubType(timeType) {
	if (timeType === "month") return addMonths;
	if (timeType === "year") return addYears;
	if (timeType === "day") return addDays;
	throw new Error("timeType must be month, year or day. Got " + String(timeType));
}
const TAIPEI_TIMEZONE = "+08";
function getTaipeiTime(utc) {
	return utcToZonedTime(utc, TAIPEI_TIMEZONE);
}
function getTaipeiNowTime() {
	return getTaipeiTime(NowTime.get());
}
function formatTaipeiTime(date, pattern) {
	return formatInTimeZone(date, TAIPEI_TIMEZONE, pattern);
}
function createTaipeiDateFunc(func) {
	return (...args) => zonedTimeToUtc(func(...args), TAIPEI_TIMEZONE);
}
const startOfDayTaipei = createTaipeiDateFunc(startOfDay);
const endOfDayTaipei = createTaipeiDateFunc(endOfDay);
const startOfYesterdayTaipei = createTaipeiDateFunc(startOfYesterday);
const endOfYesterdayTaipei = createTaipeiDateFunc(endOfYesterday);
const startOfWeekTaipei = createTaipeiDateFunc(startOfWeek);
const endOfWeekTaipei = createTaipeiDateFunc(endOfWeek);
const startOfMonthTaipei = createTaipeiDateFunc(startOfMonth);
const endOfMonthTaipei = createTaipeiDateFunc(endOfMonth);
const startOfYearTaipei = createTaipeiDateFunc(startOfYear);
function setToday() {
	return {
		start: startOfDay(/* @__PURE__ */ new Date()),
		end: endOfDay(/* @__PURE__ */ new Date())
	};
}
function setYesterday() {
	const yesterday = subDays(/* @__PURE__ */ new Date(), 1);
	return {
		start: startOfDay(yesterday),
		end: endOfDay(yesterday)
	};
}
function setTomorrow() {
	const tomorrow = addDays(/* @__PURE__ */ new Date(), 1);
	return {
		start: startOfDay(tomorrow),
		end: endOfDay(tomorrow)
	};
}
function setLastSevenDays() {
	return {
		start: startOfDay(subDays(/* @__PURE__ */ new Date(), 6)),
		end: endOfDay(/* @__PURE__ */ new Date())
	};
}
function setThisWeek() {
	return {
		start: startOfWeek(/* @__PURE__ */ new Date(), { weekStartsOn: 1 }),
		end: endOfDay(/* @__PURE__ */ new Date())
	};
}
function setLastWeek() {
	const oneDayInLastWeek = subWeeks(/* @__PURE__ */ new Date(), 1);
	return {
		start: startOfWeek(oneDayInLastWeek, { weekStartsOn: 1 }),
		end: endOfWeek(oneDayInLastWeek, { weekStartsOn: 1 })
	};
}
function setThisMonth() {
	return {
		start: startOfMonth(/* @__PURE__ */ new Date()),
		end: endOfDay(/* @__PURE__ */ new Date())
	};
}
function setThisMonthToYesterday() {
	return {
		start: startOfMonth(/* @__PURE__ */ new Date()),
		end: endOfDay(subDays(/* @__PURE__ */ new Date(), 1))
	};
}
function setLastMonth() {
	const oneDayInLastMonth = subMonths(/* @__PURE__ */ new Date(), 1);
	return {
		start: startOfMonth(oneDayInLastMonth),
		end: endOfMonth(oneDayInLastMonth)
	};
}
function setLastTwoMonths() {
	return {
		start: startOfDay(subMonths(/* @__PURE__ */ new Date(), 2)),
		end: endOfDay(/* @__PURE__ */ new Date())
	};
}
function setUpcomingDays(diff) {
	return {
		start: startOfDay(/* @__PURE__ */ new Date()),
		end: endOfDay(addDays(/* @__PURE__ */ new Date(), diff))
	};
}
function setUpcomingDaysFromNow(diff, start = Date.now()) {
	return {
		start,
		end: addDays(start, diff)
	};
}
function setUpcomingHoursFromNow(diff, start = Date.now()) {
	return {
		start,
		end: addHours(start, diff)
	};
}
function setPermanent() {
	return {
		start: /* @__PURE__ */ new Date(),
		end: endOfDay(/* @__PURE__ */ new Date(PERMANENT_TIME))
	};
}
function getAllowedTimeStamps(startTimeStamp, endTimeStamp, range = 3, timeType = "month") {
	const subFn = getSubFnBySubType(timeType);
	const addFn = getAddFnBySubType(timeType);
	let maxAllowedEndTimeStamp = 0;
	if (isLastDayOfMonth(startTimeStamp)) maxAllowedEndTimeStamp = getTime(endOfDay(endOfMonth(addFn(new Date(startTimeStamp), range))));
	else maxAllowedEndTimeStamp = getTime(endOfDay(addFn(new Date(startTimeStamp), range)));
	return {
		minAllowedStartTimeStamp: getTime(startOfDay(subFn(new Date(endTimeStamp), range))),
		maxAllowedEndTimeStamp
	};
}
function createShouldDisableFuture() {
	const shouldDisableStartDate = (day) => {
		if (day === null) return true;
		return isAfter(day, endOfDay(/* @__PURE__ */ new Date()));
	};
	const shouldDisableEndDate = (day) => {
		if (day === null) return true;
		return isAfter(day, endOfDay(/* @__PURE__ */ new Date()));
	};
	return {
		shouldDisableStartDate,
		shouldDisableEndDate
	};
}
function getCorrectedDateRange(value, maxMonthsRange = 3) {
	if (!value.start || !value.end) return value;
	if (isAfter(value.end, addMonths(value.start, maxMonthsRange))) {
		if (value.key === "start") return {
			start: value.start,
			end: endOfDay(addMonths(value.start, maxMonthsRange))
		};
		else return {
			start: startOfDay(subMonths(value.end, maxMonthsRange)),
			end: value.end
		};
	}
	return value;
}
function createShouldDisableDateBetween(startTime, endTime, range = 3, timeType = "month") {
	const subFn = getSubFnBySubType(timeType);
	const addFn = getAddFnBySubType(timeType);
	const today = endOfDay(/* @__PURE__ */ new Date());
	const shouldDisableStartDate = (day) => {
		if (day === null) return true;
		if (isAfter(day, today)) return true;
		if (endTime === null) return false;
		if (isAfter(day, endOfDay(endTime))) return true;
		const earliestAllowedDate = startOfDay(subFn(endTime, range));
		return isBefore(day, earliestAllowedDate);
	};
	const shouldDisableEndDate = (day) => {
		if (day === null) return true;
		if (isAfter(day, today)) return true;
		if (startTime === null) return false;
		if (isBefore(day, startOfDay(startTime))) return true;
		const latestAllowedDate = endOfDay(addFn(startTime, range));
		if (isAfter(day, latestAllowedDate)) return true;
		return false;
	};
	return {
		shouldDisableStartDate,
		shouldDisableEndDate
	};
}
function createShouldDisableDate(startTime, endTime, mustAfter, mustBefore) {
	const shouldDisableStartDate = (day) => {
		if (day === null) return true;
		if (isBefore(day, mustAfter)) return true;
		if (mustBefore && isAfter(day, endOfDay(mustBefore))) return true;
		if (endTime === null) return false;
		return isAfter(day, endOfDay(endTime));
	};
	const shouldDisableEndDate = (day) => {
		if (day === null) return true;
		if (mustBefore && isAfter(day, endOfDay(mustBefore))) return true;
		if (startTime === null) return isBefore(day, mustAfter);
		return isBefore(day, startOfDay(startTime));
	};
	return {
		shouldDisableStartDate,
		shouldDisableEndDate
	};
}
function toTimezonedDate(date) {
	return utcToZonedTime(date, "+0800");
}
function getMonthArray(start, end) {
	const monthStart = startOfMonth(start);
	const monthEnd = startOfMonth(end);
	let months = [];
	for (let monthCursor = monthStart; isBefore(monthCursor, monthEnd) || isEqual(monthCursor, monthEnd); monthCursor = addMonths(monthCursor, 1)) months = months.concat(monthCursor);
	return months;
}
function getWeekArray(start, end) {
	const weekStart = startOfWeek(start, { weekStartsOn: 1 });
	const weekEnd = startOfWeek(end, { weekStartsOn: 1 });
	let weeks = [];
	for (let weekCursor = weekStart; isBefore(weekCursor, weekEnd) || isEqual(weekCursor, weekEnd); weekCursor = addWeeks(weekCursor, 1)) weeks = weeks.concat(weekCursor);
	return weeks;
}
function getDayArray(start, end) {
	const dayStart = startOfDay(start);
	const dayEnd = startOfDay(end);
	let days = [];
	for (let dayCursor = dayStart; isBefore(dayCursor, dayEnd) || isEqual(dayCursor, dayEnd); dayCursor = addDays(dayCursor, 1)) days = days.concat(dayCursor);
	return days;
}
//#endregion
//#region src/utils/DateQuery.ts
const getTaipeiCalendarTimestampRangeOfToday = () => {
	const taipeiNow = getTaipeiNowTime();
	return [startOfDay(taipeiNow).getTime(), endOfDay(taipeiNow).getTime()];
};
const getTaipeiCalendarTimestampRangeOfYesterday = () => {
	const taipeiYesterday = subDays(getTaipeiNowTime(), 1);
	return [startOfDay(taipeiYesterday).getTime(), endOfDay(taipeiYesterday).getTime()];
};
const getTaipeiCalendarTimestampRangeOfThisMonthToToday = () => {
	const taipeiNow = getTaipeiNowTime();
	return [startOfMonth(taipeiNow).getTime(), endOfDay(taipeiNow).getTime()];
};
const getTaipeiDefaultDateRangeOfToday = () => [startOfDayTaipei(getTaipeiNowTime()), endOfDayTaipei(getTaipeiNowTime())];
const getDefaultDateRangeOfToday = () => [startOfDay(NowTime.get()), endOfDay(NowTime.get())];
const getTaipeiRangeOfYesterday = () => {
	const taipeiYesterday = subDays(getTaipeiNowTime(), 1);
	return [startOfDayTaipei(taipeiYesterday), endOfDayTaipei(taipeiYesterday)];
};
const getRangeOfYesterday = () => [startOfDay(subDays(/* @__PURE__ */ new Date(), 1)), endOfDay(subDays(/* @__PURE__ */ new Date(), 1))];
const getAllowDateRangeOfOneMonths = () => ({
	from: subMonths(endOfDay(NowTime.get()), 1),
	to: endOfDay(NowTime.get())
});
const getTaipeiAllowDateRangeOfTwoWeeks = () => ({
	from: subWeeks(endOfDayTaipei(getTaipeiNowTime()), 2),
	to: endOfDayTaipei(getTaipeiNowTime())
});
const getAllowDateRangeOfTwoWeeks = () => ({
	from: subWeeks(endOfDay(NowTime.get()), 2),
	to: endOfDay(NowTime.get())
});
const getAllowOffsetDaysRange = (offsetDays) => ({
	from: subDays(endOfDay(NowTime.get()), offsetDays),
	to: endOfDay(NowTime.get())
});
const getTaipeiAllowOffsetDaysRange = (offsetDays) => ({
	from: subDays(endOfDayTaipei(getTaipeiNowTime()), offsetDays),
	to: endOfDayTaipei(getTaipeiNowTime())
});
const getDateQuery = (dateRange) => ({
	start: getUnixTime(dateRange[0]),
	end: getUnixTime(isBefore(endOfDay(dateRange[1]), NowTime.get()) ? endOfDay(dateRange[1]) : NowTime.get())
});
const getDateAtQuery = (dateRange) => ({
	start_at: getUnixTime(dateRange[0]),
	end_at: getUnixTime(isBefore(endOfDay(dateRange[1]), NowTime.get()) ? endOfDay(dateRange[1]) : NowTime.get())
});
const getThisWeek = () => [startOfWeek(NowTime.get(), { weekStartsOn: 1 }), endOfDay(NowTime.get())];
const getTaipeiThisWeek = () => [startOfWeekTaipei(getTaipeiNowTime(), { weekStartsOn: 1 }), endOfDayTaipei(getTaipeiNowTime())];
const getLastWeek = () => [startOfWeek(subWeeks(NowTime.get(), 1), { weekStartsOn: 1 }), endOfWeek(subWeeks(NowTime.get(), 1), { weekStartsOn: 1 })];
const getTaipeiLastWeek = () => [startOfWeekTaipei(subWeeks(getTaipeiNowTime(), 1), { weekStartsOn: 1 }), endOfWeekTaipei(subWeeks(getTaipeiNowTime(), 1), { weekStartsOn: 1 })];
const getThisMonth = () => [startOfMonth(NowTime.get()), endOfDay(NowTime.get())];
//#endregion
//#region src/utils/EditableSystemActivitiesHelper.ts
const isEditableSystemActivitiesHelper = (type) => type !== "" && [ActivityType.WEEKLY_BONUS_GOLD].includes(type);
//#endregion
//#region \0@oxc-project+runtime@0.154.0/helpers/esm/typeof.js
function _typeof(o) {
	"@babel/helpers - typeof";
	return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function(o) {
		return typeof o;
	} : function(o) {
		return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o;
	}, _typeof(o);
}
//#endregion
//#region \0@oxc-project+runtime@0.154.0/helpers/esm/toPrimitive.js
function toPrimitive(t, r) {
	if ("object" != _typeof(t) || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != _typeof(i)) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
//#endregion
//#region \0@oxc-project+runtime@0.154.0/helpers/esm/toPropertyKey.js
function toPropertyKey(t) {
	var i = toPrimitive(t, "string");
	return "symbol" == _typeof(i) ? i : i + "";
}
//#endregion
//#region \0@oxc-project+runtime@0.154.0/helpers/esm/defineProperty.js
function _defineProperty(e, r, t) {
	return (r = toPropertyKey(r)) in e ? Object.defineProperty(e, r, {
		value: t,
		enumerable: !0,
		configurable: !0,
		writable: !0
	}) : e[r] = t, e;
}
//#endregion
//#region src/utils/IdleLogout.ts
var IdleStorage = class IdleStorage {
	constructor(key) {
		_defineProperty(this, "idleStorage", void 0);
		_defineProperty(this, "key", void 0);
		this.key = key;
		this.idleStorage = this.createStorage();
	}
	static getInstance(key) {
		if (!IdleStorage.instance) IdleStorage.instance = new IdleStorage(key);
		return IdleStorage.instance;
	}
	static reset() {
		IdleStorage.instance = null;
	}
	createStorage() {
		let fallbackLastActiveTime = null;
		return {
			get lastActiveTime() {
				if (!IdleStorage.instance) return null;
				try {
					var _window$localStorage$;
					return Number((_window$localStorage$ = window.localStorage.getItem(IdleStorage.instance.key)) !== null && _window$localStorage$ !== void 0 ? _window$localStorage$ : getUnixTime(/* @__PURE__ */ new Date()));
				} catch (_unused) {
					var _fallbackLastActiveTi;
					return (_fallbackLastActiveTi = fallbackLastActiveTime) !== null && _fallbackLastActiveTi !== void 0 ? _fallbackLastActiveTi : getUnixTime(/* @__PURE__ */ new Date());
				}
			},
			set lastActiveTime(value) {
				if (!IdleStorage.instance) return;
				try {
					window.localStorage.setItem(IdleStorage.instance.key, value.toString());
				} catch (_unused2) {
					fallbackLastActiveTime = value;
				}
			},
			clear() {
				if (!IdleStorage.instance) return;
				try {
					var _window$localStorage;
					(_window$localStorage = window.localStorage) === null || _window$localStorage === void 0 || _window$localStorage.removeItem(IdleStorage.instance.key);
				} catch (_unused3) {
					fallbackLastActiveTime = null;
				}
			}
		};
	}
	updateActiveTime() {
		this.idleStorage.lastActiveTime = getUnixTime(/* @__PURE__ */ new Date());
	}
	isIdle(idle) {
		if (!this.idleStorage.lastActiveTime) return false;
		return getUnixTime(/* @__PURE__ */ new Date()) - this.idleStorage.lastActiveTime > idle;
	}
	clear() {
		this.idleStorage.clear();
	}
	getKey() {
		return this.key;
	}
};
_defineProperty(IdleStorage, "instance", null);
const getIdleStorage = (key) => {
	return IdleStorage.getInstance(key);
};
const registerIdleLogout = (payload) => {
	const { isLoginSubject, logout, idle = 7200, key = "IDLE:LAST_ACTIVE_TIME_KEY_V4" } = payload;
	const storage = IdleStorage.getInstance(key);
	const checkIdleLogout = () => {
		if (storage.isIdle(idle)) {
			logout();
			storage.clear();
			return;
		}
		storage.updateActiveTime();
	};
	const subscription = isLoginSubject.subscribe((isLogin) => {
		if (isLogin) {
			checkIdleLogout();
			window.addEventListener("click", checkIdleLogout, { passive: true });
		} else {
			window.removeEventListener("click", checkIdleLogout);
			storage.clear();
		}
	});
	return () => {
		if (!subscription.closed) subscription.unsubscribe();
		window.removeEventListener("click", checkIdleLogout);
	};
};
//#endregion
//#region src/utils/Image.ts
const TRANSPARENT_IMAGE = `data:image/png;base64,iVBORw0KGgoAAAANSUhEUg
AAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=`;
//#endregion
//#region src/utils/PlayerRankImage.ts
const getPlayerRankImage = (rank) => {
	return new URL(`./assets/rank/icons/${rank}.png`, import.meta.url).href;
};
//#endregion
//#region src/utils/announcementIcon.ts
const announcementIcon = (type, isRead = false) => {
	return new URL(`./assets/message/${type}/${isRead ? "read" : "unread"}.svg`, import.meta.url).href;
};
//#endregion
//#region src/utils/copyString.ts
const copyString = (content) => {
	const node = document.createElement("textarea");
	node.value = content;
	document.body.append(node);
	node.select();
	document.execCommand("copy");
	node.remove();
};
//#endregion
//#region src/utils/downloadFile.ts
const EXTENSION_PATTERN = /\.[a-z0-9]+$/i;
const getExtension = (response) => {
	var _response$headers$get, _exec$, _exec;
	const disposition = (_response$headers$get = response.headers.get("content-disposition")) !== null && _response$headers$get !== void 0 ? _response$headers$get : "";
	return (_exec$ = (_exec = /filename\*?=(?:UTF-8'')?"?[^";]*(\.[a-z0-9]+)"?/i.exec(disposition)) === null || _exec === void 0 ? void 0 : _exec[1]) !== null && _exec$ !== void 0 ? _exec$ : ".jpg";
};
const downloadFile = async (url, fileName) => {
	try {
		const response = await fetch(url);
		if (!response.ok) throw new Error(`download failed with status ${response.status}`);
		const objectUrl = URL.createObjectURL(await response.blob());
		const anchor = document.createElement("a");
		anchor.href = objectUrl;
		anchor.download = EXTENSION_PATTERN.test(fileName) ? fileName : `${fileName}${getExtension(response)}`;
		anchor.click();
		setTimeout(() => {
			URL.revokeObjectURL(objectUrl);
		});
	} catch (_unused) {
		window.open(url, "_blank", "noopener,noreferrer");
	}
};
//#endregion
//#region src/utils/goldenCashFormat.ts
function goldenCashFormat(value) {
	if (value === "") return "";
	return BcMath.base(value).goldenFormat();
}
//#endregion
//#region src/utils/htmlToCanvas.ts
const htmlToCanvas = html2canvas;
const canvasToImage = (canvas) => canvas.toDataURL();
//#endregion
//#region src/utils/loadScript.ts
const loadScript = async (url, preconnect = true) => {
	return await new Promise((resolve, reject) => {
		const head = document.head || document.getElementsByTagName("head")[0];
		const script = document.createElement("script");
		script.async = true;
		script.src = url;
		if (preconnect) {
			const link = document.createElement("link");
			const { origin } = new URL(url);
			link.href = origin;
			link.rel = "preconnect";
			head.appendChild(link);
		}
		head.appendChild(script);
		script.onload = resolve;
		script.onerror = reject;
	});
};
//#endregion
//#region src/utils/normalize.ts
function normalizeMaxNumber(data, max, removeZero = true) {
	let result = data.replace(/[^0-9]/g, "");
	if (removeZero) result = result.replace(/^0+/, "");
	if (result !== "" && Number(BcMath.base(result).comp(max)) === 1) result = max;
	return result;
}
function normalizeMinNumber(data, min, removeZero = true) {
	let result = data.replace(/[^0-9]/g, "");
	if (removeZero) result = result.replace(/^0+/, "");
	if (result.length === 0 || Number(BcMath.base(result).comp(min)) === -1) result = min;
	return result;
}
function getMaxInteger(cash) {
	return cash.split(".").length > 0 ? cash.split(".")[0] : cash;
}
function filterHTMLTags(html) {
	return html.replace(/\n/g, " ").replace(/<[^>]*>/g, "").replace(/&gt;/g, ">").replace(/&lt;/g, "<");
}
function recursivelyParseJSON(obj) {
	for (const key in obj) {
		const value = obj[key];
		if (typeof value === "string") try {
			const nested = JSON.parse(value);
			if (typeof nested === "object" && nested !== null) obj[key] = recursivelyParseJSON(nested);
		} catch (_unused) {}
		else if (typeof value === "object" && value !== null) obj[key] = recursivelyParseJSON(value);
	}
	return obj;
}
function normalizeAlphanumeric(data) {
	return data.replace(/[^a-zA-Z0-9]/g, "");
}
function normalizeNestedJson(jsonString, whiteSpace = 2) {
	if (!jsonString) return "";
	try {
		const processedObject = recursivelyParseJSON(JSON.parse(jsonString));
		return JSON.stringify(processedObject, null, whiteSpace);
	} catch (error) {
		console.error("Failed to parse initial JSON string:", error instanceof Error ? error.message : error);
		return "";
	}
}
//#endregion
//#region src/utils/openQQ.ts
const openQQ = (id) => {
	const ua = navigator.userAgent;
	const isIphone = !!!ua.match(/(iPad).*OS\s([\d_]+)/) && !!ua.match(/(iPhone\sOS)\s([\d_]+)/);
	const isAndroid = !!ua.match(/(Android)\s+([\d.]+)/);
	if (isIphone) location.href = `mqqapi://card/show_pslcard?src_type=internal&version=1&uin=${id}&card_type=person&source=sharecard`;
	else if (isAndroid) location.href = `mqqapi://card/show_pslcard?src_type=internal&version=1&uin=${id}&card_type=person&source=sharecard`;
	else location.href = `tencent://AddContact/?fromId=45&fromSubId=1&subcmd=all&uin=${id}`;
};
//#endregion
//#region src/utils/openWindow.ts
const openWindow = (name, href, width = 500, height = 500, windowFeature = `
location=yes,
resizable=yes,
scrollbars=yes,
status=yes,
width=${width},
height=${height}
`) => {
	const win = window.open(href, name, windowFeature);
	if (!win) throw new Error("Can't open new window.");
	return win;
};
const LOADING_HTML = "<center><p style=\"font-size: 24px;\">Loading...</p></center>";
const setWindowLoading = (win, html = LOADING_HTML) => {
	try {
		var _win$document$getElem;
		const meta1 = win.document.createElement("meta");
		meta1.setAttribute("name", "viewport");
		meta1.setAttribute("content", "width=device-width,initial-scale=1,maximum-scale=1,minimum-scale=1,user-scalable=no,viewport-fit=cover");
		((_win$document$getElem = win.document.getElementsByTagName("head")) === null || _win$document$getElem === void 0 ? void 0 : _win$document$getElem[0]).appendChild(meta1);
		win.document.body.innerHTML = html;
	} catch (e) {
		console.error(e);
	}
};
//#endregion
//#region src/utils/scrollIntoView.ts
function scrollIntoView(dom) {
	if (!dom) return;
	const [x, y] = [window.scrollX, window.scrollY];
	dom.scrollIntoView();
	window.scrollTo(x, y);
}
//#endregion
//#region src/utils/scrollMore.ts
const scrollMore = (loadMore, tolerance = 10) => (e) => {
	const target = e.target;
	if (target.scrollTop + target.clientHeight < target.scrollHeight - tolerance) return;
	loadMore();
};
//#endregion
//#region src/utils/setViewportOnDesktop.ts
function setViewportOnDesktop() {
	if (window.document.body.clientWidth >= 1280) return;
	const viewportMeta = document.querySelector("meta[name=viewport]");
	viewportMeta === null || viewportMeta === void 0 || viewportMeta.setAttribute("content", "width=1280");
}
//#endregion
//#region src/utils/splitTextByUrl.ts
const SAFE_PROTOCOLS = ["http:", "https:"];
/**
* 從 http(s):// 開始往後吃，遇到排除集合裡的字元就停。
*
* 例：他说"https://x.com?a=1"对吧 → 只吃到 https://x.com?a=1，遇到 " 就停。
* 排除中文與全形標點，是因為中文常常直接接在網址後面不加空白。
*/
const URL_CANDIDATE = /* @__PURE__ */ new RegExp("https?://[^\\s\"'<>\\u3000-\\u303F\\uFF00-\\uFFEF\\u4E00-\\u9FFF]+", "g");
const TRAILING_PUNCTUATION = /[.,;:!?)\]}]+$/;
const isSafeUrl = (text) => {
	try {
		return SAFE_PROTOCOLS.includes(new URL(text).protocol);
	} catch (_unused) {
		return false;
	}
};
/**
* 把訊息文字切成純文字與網址兩種段落，讓畫面能把網址渲染成連結。
* regex 只負責找出候選區間，能不能當連結仍然由解析器決定。
*/
const splitTextByUrl = (text) => {
	const segments = [];
	let lastIndex = 0;
	for (const match of text.matchAll(URL_CANDIDATE)) {
		const url = match[0].replace(TRAILING_PUNCTUATION, "");
		if (!isSafeUrl(url)) continue;
		const start = match.index;
		if (start === void 0) continue;
		if (start > lastIndex) segments.push({
			text: text.slice(lastIndex, start),
			isUrl: false
		});
		segments.push({
			text: url,
			isUrl: true
		});
		lastIndex = start + url.length;
	}
	if (lastIndex < text.length) segments.push({
		text: text.slice(lastIndex),
		isUrl: false
	});
	return segments;
};
//#endregion
//#region src/utils/supportWebp.ts
const supportWebp = () => {
	var _canvas$getContext;
	const canvas = document.createElement("canvas");
	return ((_canvas$getContext = canvas.getContext) === null || _canvas$getContext === void 0 ? void 0 : _canvas$getContext.call(canvas, "2d")) && !canvas.toDataURL("image/webp").indexOf("data:image/webp");
};
const getWebpClass = () => supportWebp() ? "webp" : "no-webp";
//#endregion
//#region src/utils/tokenRefreshHandler.ts
function createTokenRefreshHandler(refreshToken) {
	let refreshSubject$ = null;
	return (error) => {
		if (!refreshSubject$) {
			refreshSubject$ = new Subject();
			refreshToken().subscribe({
				next: () => {
					refreshSubject$ === null || refreshSubject$ === void 0 || refreshSubject$.next();
					refreshSubject$ === null || refreshSubject$ === void 0 || refreshSubject$.complete();
					refreshSubject$ = null;
				},
				error: () => {
					refreshSubject$ === null || refreshSubject$ === void 0 || refreshSubject$.error(error);
					refreshSubject$ = null;
				}
			});
		}
		return refreshSubject$.asObservable();
	};
}
//#endregion
//#region src/cashflow/depositMethods/interface/DepositMethodAction.ts
let ActionKind = /* @__PURE__ */ function(ActionKind) {
	ActionKind[ActionKind["COMPONENT"] = 0] = "COMPONENT";
	ActionKind[ActionKind["JS"] = 1] = "JS";
	return ActionKind;
}({});
//#endregion
//#region src/cashflow/depositMethods/DepositMethodBase.ts
var DepositMethodBase = class {
	constructor(openWindowAction, vt = (key) => key) {
		this.openWindowAction = openWindowAction;
		this.vt = vt;
		_defineProperty(this, "_commonAction", {
			[DepositActionType.ACCESS]: {
				kind: 1,
				name: "OpenWindow",
				script: (data, argument) => this.openWindowAction(data, argument),
				argument: {
					width: 500,
					height: 500,
					title: () => this.vt(`depositMethod.${this.id}.name`)
				}
			},
			[DepositActionType.QRCODE]: {
				kind: 0,
				name: "QRCodeModal",
				argument: { account: "" }
			},
			[DepositActionType.QRCODE_GOUBAO]: {
				kind: 0,
				name: "GoubaoQRCodeModal",
				argument: null
			},
			[DepositActionType.SHOW_BANK]: {
				kind: 0,
				name: "ShowInfoModal",
				argument: null
			},
			[DepositActionType.SHOW_CRYPTO]: {
				kind: 0,
				name: "CryptoInfoModal",
				argument: null
			},
			[DepositActionType.PENDING]: {
				kind: 0,
				name: "Pending",
				argument: null
			}
		});
	}
	getComponents() {
		return this._components;
	}
	getActionWrapper(forwarderId, actionId) {
		if (forwarderId in this._specialActionComponents && actionId in this._specialActionComponents[forwarderId]) return this._specialActionComponents[forwarderId][actionId];
		return this._commonAction[actionId];
	}
};
//#endregion
//#region src/cashflow/depositMethods/CGPay.ts
var DepositCGPay = class extends DepositMethodBase {
	constructor(..._args) {
		super(..._args);
		_defineProperty(this, "id", DepositMethodType.CGPAY);
		_defineProperty(this, "bindCardRequired", false);
		_defineProperty(this, "hasDownloadLink", true);
		_defineProperty(this, "openAgentLayer", true);
		_defineProperty(this, "_components", {
			middle: { name: "DepositCGPayApplyBlock" },
			bottom: { name: "DepositCGPayNoteBlock" }
		});
		_defineProperty(this, "_specialActionComponents", {});
	}
};
//#endregion
//#region src/cashflow/depositMethods/CryptoErc.ts
var DepositCryptoErc = class extends DepositMethodBase {
	constructor(..._args) {
		super(..._args);
		_defineProperty(this, "id", DepositMethodType.CRYPTO_ERC);
		_defineProperty(this, "bindCardRequired", false);
		_defineProperty(this, "hasDownloadLink", false);
		_defineProperty(this, "openAgentLayer", true);
		_defineProperty(this, "_components", {
			middle: { name: "DepositCryptoApplyBlock" },
			bottom: { name: "DepositCryptoNoteBlock" }
		});
		_defineProperty(this, "_specialActionComponents", {});
	}
};
//#endregion
//#region src/cashflow/depositMethods/CryptoTrc.ts
var DepositCryptoTrc = class extends DepositMethodBase {
	constructor(..._args) {
		super(..._args);
		_defineProperty(this, "id", DepositMethodType.CRYPTO_TRC);
		_defineProperty(this, "bindCardRequired", false);
		_defineProperty(this, "hasDownloadLink", false);
		_defineProperty(this, "openAgentLayer", true);
		_defineProperty(this, "_components", {
			middle: { name: "DepositCryptoApplyBlock" },
			bottom: { name: "DepositCryptoNoteBlock" }
		});
		_defineProperty(this, "_specialActionComponents", {});
	}
};
//#endregion
//#region src/cashflow/depositMethods/ScanWechat.ts
var DepositScanWechat = class extends DepositMethodBase {
	constructor(..._args) {
		super(..._args);
		_defineProperty(this, "id", DepositMethodType.SCAN_WECHAT);
		_defineProperty(this, "bindCardRequired", false);
		_defineProperty(this, "hasDownloadLink", false);
		_defineProperty(this, "openAgentLayer", false);
		_defineProperty(this, "_components", {
			middle: { name: "DepositApplyBlock" },
			bottom: { name: "DepositOnlineScanNoteBlock" }
		});
		_defineProperty(this, "_specialActionComponents", {});
		_defineProperty(this, "_commonAction", {
			...this._commonAction,
			[DepositActionType.QRCODE]: {
				kind: 0,
				name: "QRCodeModal",
				argument: { account: this.vt("depositMethod.scan_wechat.qrcode.account") }
			}
		});
	}
};
//#endregion
//#region src/cashflow/depositMethods/ScanAlipay.ts
var DepositScanAlipay = class extends DepositMethodBase {
	constructor(..._args) {
		super(..._args);
		_defineProperty(this, "id", DepositMethodType.SCAN_ALIPAY);
		_defineProperty(this, "bindCardRequired", false);
		_defineProperty(this, "hasDownloadLink", false);
		_defineProperty(this, "openAgentLayer", true);
		_defineProperty(this, "_components", {
			middle: { name: "DepositApplyBlock" },
			bottom: { name: "DepositOnlineScanNoteBlock" }
		});
		_defineProperty(this, "_specialActionComponents", {});
		_defineProperty(this, "_commonAction", {
			...this._commonAction,
			[DepositActionType.QRCODE]: {
				kind: 0,
				name: "QRCodeModal",
				argument: { account: this.vt("depositMethod.scan_alipay.qrcode.account") }
			}
		});
	}
};
//#endregion
//#region src/cashflow/depositMethods/Online.ts
var DepositOnline = class extends DepositMethodBase {
	constructor(..._args) {
		super(..._args);
		_defineProperty(this, "id", DepositMethodType.ONLINE);
		_defineProperty(this, "bindCardRequired", true);
		_defineProperty(this, "hasDownloadLink", false);
		_defineProperty(this, "openAgentLayer", true);
		_defineProperty(this, "_components", {
			middle: { name: "DepositApplyBlock" },
			bottom: { name: "DepositOnlineScanNoteBlock" }
		});
		_defineProperty(this, "_specialActionComponents", {});
	}
};
//#endregion
//#region src/cashflow/depositMethods/OnlineManual.ts
var DepositOnlineManual = class extends DepositMethodBase {
	constructor(..._args) {
		super(..._args);
		_defineProperty(this, "id", DepositMethodType.ONLINE_MANUAL);
		_defineProperty(this, "bindCardRequired", false);
		_defineProperty(this, "hasDownloadLink", false);
		_defineProperty(this, "openAgentLayer", false);
		_defineProperty(this, "_components", {
			middle: { name: "DepositApplyBlock" },
			bottom: { name: "DepositOnlineScanNoteBlock" }
		});
		_defineProperty(this, "_specialActionComponents", {});
	}
};
//#endregion
//#region src/cashflow/depositMethods/Goubao.ts
var DepositGoubao = class extends DepositMethodBase {
	constructor(..._args) {
		super(..._args);
		_defineProperty(this, "id", DepositMethodType.GOUBAO);
		_defineProperty(this, "bindCardRequired", false);
		_defineProperty(this, "hasDownloadLink", true);
		_defineProperty(this, "openAgentLayer", false);
		_defineProperty(this, "_components", {
			middle: { name: "DepositGoubaoApplyBlock" },
			bottom: { name: "DepositGoubaoNoteBlock" }
		});
		_defineProperty(this, "_specialActionComponents", {});
		_defineProperty(this, "_commonAction", {
			...this._commonAction,
			[DepositActionType.QRCODE_GOUBAO]: {
				kind: 0,
				name: "GoubaoQRCodeModal",
				argument: null
			}
		});
	}
};
//#endregion
//#region src/cashflow/depositMethods/Topay.ts
var DepositTopay = class extends DepositMethodBase {
	constructor(..._args) {
		super(..._args);
		_defineProperty(this, "id", DepositMethodType.TOPAY);
		_defineProperty(this, "bindCardRequired", false);
		_defineProperty(this, "hasDownloadLink", true);
		_defineProperty(this, "openAgentLayer", true);
		_defineProperty(this, "_components", {
			middle: { name: "DepositApplyBlock" },
			bottom: { name: "DepositTopayNoteBlock" }
		});
		_defineProperty(this, "_specialActionComponents", {});
	}
};
//#endregion
//#region src/cashflow/depositMethods/Ebpay.ts
var DepositEbpay = class extends DepositMethodBase {
	constructor(..._args) {
		super(..._args);
		_defineProperty(this, "id", DepositMethodType.EBPAY);
		_defineProperty(this, "bindCardRequired", false);
		_defineProperty(this, "hasDownloadLink", true);
		_defineProperty(this, "openAgentLayer", true);
		_defineProperty(this, "_components", {
			middle: { name: "DepositApplyBlock" },
			bottom: { name: "DepositEbpayNoteBlock" }
		});
		_defineProperty(this, "_specialActionComponents", {});
	}
};
//#endregion
//#region src/cashflow/depositMethods/UnionPayQuick.ts
var DepositUnionPayQuick = class extends DepositMethodBase {
	constructor(..._args) {
		super(..._args);
		_defineProperty(this, "id", DepositMethodType.UNION_PAY_QUICK);
		_defineProperty(this, "bindCardRequired", true);
		_defineProperty(this, "hasDownloadLink", false);
		_defineProperty(this, "openAgentLayer", true);
		_defineProperty(this, "_components", {
			middle: { name: "DepositApplyBlock" },
			bottom: { name: "DepositUnionPayCommonNoteBlock" }
		});
		_defineProperty(this, "_specialActionComponents", {});
	}
};
//#endregion
//#region src/cashflow/depositMethods/KOIPay.ts
var DepositKOIPay = class extends DepositMethodBase {
	constructor(..._args) {
		super(..._args);
		_defineProperty(this, "id", DepositMethodType.KOIPAY);
		_defineProperty(this, "bindCardRequired", false);
		_defineProperty(this, "hasDownloadLink", true);
		_defineProperty(this, "openAgentLayer", true);
		_defineProperty(this, "_components", {
			middle: { name: "DepositApplyBlock" },
			bottom: { name: "DepositKoipayNoteBlock" }
		});
		_defineProperty(this, "_specialActionComponents", {});
	}
};
//#endregion
//#region src/cashflow/depositMethods/ECNY.ts
var DepositECNY = class extends DepositMethodBase {
	constructor(..._args) {
		super(..._args);
		_defineProperty(this, "id", DepositMethodType.E_CNY);
		_defineProperty(this, "bindCardRequired", false);
		_defineProperty(this, "hasDownloadLink", true);
		_defineProperty(this, "openAgentLayer", true);
		_defineProperty(this, "_components", {
			middle: { name: "DepositApplyBlock" },
			bottom: { name: "DepositOnlineScanNoteBlock" }
		});
		_defineProperty(this, "_specialActionComponents", {});
	}
};
//#endregion
//#region src/cashflow/depositMethods/Pay988.ts
var DepositPay988 = class extends DepositMethodBase {
	constructor(..._args) {
		super(..._args);
		_defineProperty(this, "id", DepositMethodType.PAY988);
		_defineProperty(this, "bindCardRequired", false);
		_defineProperty(this, "hasDownloadLink", true);
		_defineProperty(this, "openAgentLayer", true);
		_defineProperty(this, "_components", {
			middle: { name: "DepositApplyBlock" },
			bottom: { name: "Deposit988payNoteBlock" }
		});
		_defineProperty(this, "_specialActionComponents", {});
	}
};
//#endregion
//#region src/cashflow/depositMethods/Pay808.ts
var DepositPay808 = class extends DepositMethodBase {
	constructor(..._args) {
		super(..._args);
		_defineProperty(this, "id", DepositMethodType.PAY808);
		_defineProperty(this, "bindCardRequired", false);
		_defineProperty(this, "hasDownloadLink", true);
		_defineProperty(this, "openAgentLayer", true);
		_defineProperty(this, "_components", {
			middle: { name: "DepositApplyBlock" },
			bottom: { name: "Deposit808payNoteBlock" }
		});
		_defineProperty(this, "_specialActionComponents", {});
	}
};
//#endregion
//#region src/cashflow/depositMethods/KDPay.ts
var DepositKDPay = class extends DepositMethodBase {
	constructor(..._args) {
		super(..._args);
		_defineProperty(this, "id", DepositMethodType.KDPAY);
		_defineProperty(this, "bindCardRequired", false);
		_defineProperty(this, "hasDownloadLink", true);
		_defineProperty(this, "openAgentLayer", true);
		_defineProperty(this, "_components", {
			middle: { name: "DepositApplyBlock" },
			bottom: { name: "DepositKdpayNoteBlock" }
		});
		_defineProperty(this, "_specialActionComponents", {});
	}
};
//#endregion
//#region src/cashflow/depositMethods/UnionPayCloud.ts
var DepositUnionPayCloud = class extends DepositMethodBase {
	constructor(..._args) {
		super(..._args);
		_defineProperty(this, "id", DepositMethodType.UNION_PAY_CLOUD);
		_defineProperty(this, "bindCardRequired", true);
		_defineProperty(this, "hasDownloadLink", false);
		_defineProperty(this, "openAgentLayer", true);
		_defineProperty(this, "_components", {
			middle: { name: "DepositApplyBlock" },
			bottom: { name: "DepositUnionPayCommonNoteBlock" }
		});
		_defineProperty(this, "_specialActionComponents", {});
	}
};
//#endregion
//#region src/cashflow/depositMethods/DepositMethodFactory.ts
const deposits = {
	[DepositMethodType.ONLINE]: (openWindowAction, vt) => new DepositOnline(openWindowAction, vt),
	[DepositMethodType.ONLINE_MANUAL]: (openWindowAction, vt) => new DepositOnlineManual(openWindowAction, vt),
	[DepositMethodType.SCAN_WECHAT]: (openWindowAction, vt) => new DepositScanWechat(openWindowAction, vt),
	[DepositMethodType.SCAN_ALIPAY]: (openWindowAction, vt) => new DepositScanAlipay(openWindowAction, vt),
	[DepositMethodType.CRYPTO_ERC]: (openWindowAction, vt) => new DepositCryptoErc(openWindowAction, vt),
	[DepositMethodType.CRYPTO_TRC]: (openWindowAction, vt) => new DepositCryptoTrc(openWindowAction, vt),
	[DepositMethodType.GOUBAO]: (openWindowAction, vt) => new DepositGoubao(openWindowAction, vt),
	[DepositMethodType.TOPAY]: (openWindowAction, vt) => new DepositTopay(openWindowAction, vt),
	[DepositMethodType.EBPAY]: (openWindowAction, vt) => new DepositEbpay(openWindowAction, vt),
	[DepositMethodType.UNION_PAY_QUICK]: (openWindowAction, vt) => new DepositUnionPayQuick(openWindowAction, vt),
	[DepositMethodType.UNION_PAY_CLOUD]: (openWindowAction, vt) => new DepositUnionPayCloud(openWindowAction, vt),
	[DepositMethodType.CGPAY]: (openWindowAction, vt) => new DepositCGPay(openWindowAction, vt),
	[DepositMethodType.KOIPAY]: (openWindowAction, vt) => new DepositKOIPay(openWindowAction, vt),
	[DepositMethodType.E_CNY]: (openWindowAction, vt) => new DepositECNY(openWindowAction, vt),
	[DepositMethodType.PAY988]: (openWindowAction, vt) => new DepositPay988(openWindowAction, vt),
	[DepositMethodType.PAY808]: (openWindowAction, vt) => new DepositPay808(openWindowAction, vt),
	[DepositMethodType.KDPAY]: (openWindowAction, vt) => new DepositKDPay(openWindowAction, vt)
};
const DepositMethodFactory = {
	create(depositId, openWindowAction, vt) {
		const instance = deposits[depositId];
		if (instance === void 0) throw new Error("No DepositMethod instance");
		return instance(openWindowAction, vt);
	},
	has(depositId) {
		return deposits[depositId] !== void 0;
	}
};
//#endregion
//#region src/cashflow/withdrawalMethods/WithdrawalMethodBase.ts
var WithdrawalMethodBase = class {
	getComponents() {
		return this._components;
	}
};
//#endregion
//#region src/cashflow/withdrawalMethods/AgentTransfer.ts
var WithdrawalAgentTransfer = class extends WithdrawalMethodBase {
	constructor(..._args) {
		super(..._args);
		_defineProperty(this, "id", WithdrawWayType.AGENT_TRANSFER_WITHDRAW);
		_defineProperty(this, "bindCardRequired", false);
		_defineProperty(this, "hasDownloadLink", false);
		_defineProperty(this, "walletCategory", "debitCard");
		_defineProperty(this, "walletLength", null);
		_defineProperty(this, "currency", CurrencyType.RMB);
		_defineProperty(this, "_components", {
			middle: { name: "WithdrawalAgentTransferApplyBlock" },
			bottom: { name: "WithdrawalNoteBlock" }
		});
	}
};
//#endregion
//#region src/cashflow/withdrawalMethods/Alipay.ts
var WithdrawalAliPay = class extends WithdrawalMethodBase {
	constructor(..._args) {
		super(..._args);
		_defineProperty(this, "id", WithdrawWayType.CURRENCY_ALIPAY);
		_defineProperty(this, "bindCardRequired", false);
		_defineProperty(this, "hasDownloadLink", false);
		_defineProperty(this, "walletCategory", "thirdParty");
		_defineProperty(this, "walletLength", 3);
		_defineProperty(this, "currency", CurrencyType.RMB);
		_defineProperty(this, "_components", {
			middle: { name: "WithdrawalAlipayApplyBlock" },
			bottom: { name: "WithdrawalNoteBlock" }
		});
	}
};
//#endregion
//#region src/cashflow/withdrawalMethods/CGPay.ts
var WithdrawalCGPay = class extends WithdrawalMethodBase {
	constructor(..._args) {
		super(..._args);
		_defineProperty(this, "id", WithdrawWayType.CURRENCY_CGPAY);
		_defineProperty(this, "bindCardRequired", false);
		_defineProperty(this, "hasDownloadLink", true);
		_defineProperty(this, "walletLength", 1);
		_defineProperty(this, "walletCategory", "thirdParty");
		_defineProperty(this, "currency", CurrencyType.RMB);
		_defineProperty(this, "_components", {
			middle: { name: "WithdrawalCGPayApplyBlock" },
			bottom: { name: "WithdrawalCGPayNoteBlock" }
		});
	}
};
//#endregion
//#region src/cashflow/withdrawalMethods/DebitCard.ts
var WithdrawalDebitCard = class extends WithdrawalMethodBase {
	constructor(..._args) {
		super(..._args);
		_defineProperty(this, "id", WithdrawWayType.CURRENCY_DEBIT_CARD);
		_defineProperty(this, "bindCardRequired", false);
		_defineProperty(this, "hasDownloadLink", false);
		_defineProperty(this, "hasExchangeRate", false);
		_defineProperty(this, "walletCategory", "debitCard");
		_defineProperty(this, "walletLength", 5);
		_defineProperty(this, "currency", CurrencyType.RMB);
		_defineProperty(this, "_components", {
			middle: { name: "WithdrawalApplyBlock" },
			bottom: { name: "WithdrawalNoteBlock" }
		});
	}
};
//#endregion
//#region src/cashflow/withdrawalMethods/Ebpay.ts
var WithdrawalEbpay = class extends WithdrawalMethodBase {
	constructor(..._args) {
		super(..._args);
		_defineProperty(this, "id", WithdrawWayType.CURRENCY_EBPAY);
		_defineProperty(this, "bindCardRequired", false);
		_defineProperty(this, "hasDownloadLink", true);
		_defineProperty(this, "walletCategory", "crypto");
		_defineProperty(this, "walletLength", 1);
		_defineProperty(this, "currency", CurrencyType.RMB);
		_defineProperty(this, "_components", {
			middle: { name: "WithdrawalCryptoApplyBlock" },
			bottom: { name: "WithdrawalEbpayNoteBlock" }
		});
	}
};
//#endregion
//#region src/cashflow/withdrawalMethods/Erc20.ts
var WithdrawalErc20 = class extends WithdrawalMethodBase {
	constructor(..._args) {
		super(..._args);
		_defineProperty(this, "id", WithdrawWayType.CURRENCY_CRYPTO_ERC);
		_defineProperty(this, "bindCardRequired", false);
		_defineProperty(this, "hasDownloadLink", false);
		_defineProperty(this, "walletCategory", "crypto");
		_defineProperty(this, "walletLength", 5);
		_defineProperty(this, "currency", CurrencyType.USDT);
		_defineProperty(this, "_components", {
			middle: { name: "WithdrawalCryptoApplyBlock" },
			bottom: { name: "WithdrawalCryptoNoteBlock" }
		});
	}
};
//#endregion
//#region src/cashflow/withdrawalMethods/Goubao.ts
var WithdrawalGoubao = class extends WithdrawalMethodBase {
	constructor(..._args) {
		super(..._args);
		_defineProperty(this, "id", WithdrawWayType.CURRENCY_GOUBAO);
		_defineProperty(this, "bindCardRequired", false);
		_defineProperty(this, "hasDownloadLink", true);
		_defineProperty(this, "walletCategory", "thirdParty");
		_defineProperty(this, "walletLength", 1);
		_defineProperty(this, "currency", CurrencyType.RMB);
		_defineProperty(this, "_components", {
			middle: { name: "WithdrawalGoubaoApplyBlock" },
			bottom: { name: "WithdrawalGoubaoNoteBlock" }
		});
	}
};
//#endregion
//#region src/cashflow/withdrawalMethods/KDPay.ts
var WithdrawalKDPay = class extends WithdrawalMethodBase {
	constructor(..._args) {
		super(..._args);
		_defineProperty(this, "id", WithdrawWayType.CURRENCY_KDPAY);
		_defineProperty(this, "bindCardRequired", false);
		_defineProperty(this, "hasDownloadLink", true);
		_defineProperty(this, "walletCategory", "crypto");
		_defineProperty(this, "walletLength", 1);
		_defineProperty(this, "currency", CurrencyType.RMB);
		_defineProperty(this, "_components", {
			middle: { name: "WithdrawalCryptoApplyBlock" },
			bottom: { name: "WithdrawalKdpayNoteBlock" }
		});
	}
};
//#endregion
//#region src/cashflow/withdrawalMethods/KOIPay.ts
var WithdrawalKOIPay = class extends WithdrawalMethodBase {
	constructor(..._args) {
		super(..._args);
		_defineProperty(this, "id", WithdrawWayType.CURRENCY_KOIPAY);
		_defineProperty(this, "bindCardRequired", false);
		_defineProperty(this, "hasDownloadLink", true);
		_defineProperty(this, "walletCategory", "crypto");
		_defineProperty(this, "walletLength", 1);
		_defineProperty(this, "currency", CurrencyType.RMB);
		_defineProperty(this, "_components", {
			middle: { name: "WithdrawalCryptoApplyBlock" },
			bottom: { name: "WithdrawalKoipayNoteBlock" }
		});
	}
};
//#endregion
//#region src/cashflow/withdrawalMethods/Pay808.ts
var WithdrawalPay808 = class extends WithdrawalMethodBase {
	constructor(..._args) {
		super(..._args);
		_defineProperty(this, "id", WithdrawWayType.CURRENCY_PAY808);
		_defineProperty(this, "bindCardRequired", false);
		_defineProperty(this, "hasDownloadLink", true);
		_defineProperty(this, "walletCategory", "crypto");
		_defineProperty(this, "walletLength", 1);
		_defineProperty(this, "currency", CurrencyType.RMB);
		_defineProperty(this, "_components", {
			middle: { name: "WithdrawalCryptoApplyBlock" },
			bottom: { name: "Withdrawal808payNoteBlock" }
		});
	}
};
//#endregion
//#region src/cashflow/withdrawalMethods/Pay988.ts
var WithdrawalPay988 = class extends WithdrawalMethodBase {
	constructor(..._args) {
		super(..._args);
		_defineProperty(this, "id", WithdrawWayType.CURRENCY_PAY988);
		_defineProperty(this, "bindCardRequired", false);
		_defineProperty(this, "hasDownloadLink", true);
		_defineProperty(this, "walletCategory", "crypto");
		_defineProperty(this, "walletLength", 1);
		_defineProperty(this, "currency", CurrencyType.RMB);
		_defineProperty(this, "_components", {
			middle: { name: "WithdrawalCryptoApplyBlock" },
			bottom: { name: "Withdrawal988payNoteBlock" }
		});
	}
};
//#endregion
//#region src/cashflow/withdrawalMethods/Topay.ts
var WithdrawalTopay = class extends WithdrawalMethodBase {
	constructor(..._args) {
		super(..._args);
		_defineProperty(this, "id", WithdrawWayType.CURRENCY_TOPAY);
		_defineProperty(this, "bindCardRequired", false);
		_defineProperty(this, "hasDownloadLink", true);
		_defineProperty(this, "hasExchangeRate", true);
		_defineProperty(this, "walletCategory", "crypto");
		_defineProperty(this, "walletLength", 1);
		_defineProperty(this, "currency", CurrencyType.RMB);
		_defineProperty(this, "_components", {
			middle: { name: "WithdrawalCryptoApplyBlock" },
			bottom: { name: "WithdrawalTopayNoteBlock" }
		});
	}
};
//#endregion
//#region src/cashflow/withdrawalMethods/Trc20.ts
var WithdrawalTrc20 = class extends WithdrawalMethodBase {
	constructor(..._args) {
		super(..._args);
		_defineProperty(this, "id", WithdrawWayType.CURRENCY_CRYPTO_ERC);
		_defineProperty(this, "bindCardRequired", false);
		_defineProperty(this, "hasDownloadLink", false);
		_defineProperty(this, "hasExchangeRate", true);
		_defineProperty(this, "walletCategory", "crypto");
		_defineProperty(this, "walletLength", 5);
		_defineProperty(this, "currency", CurrencyType.USDT);
		_defineProperty(this, "_components", {
			middle: { name: "WithdrawalCryptoApplyBlock" },
			bottom: { name: "WithdrawalCryptoNoteBlock" }
		});
	}
};
//#endregion
//#region src/cashflow/withdrawalMethods/WithdrawalMethodFactory.ts
const withdrawals = {
	[WithdrawWayType.AGENT_TRANSFER_WITHDRAW]: () => new WithdrawalAgentTransfer(),
	[WithdrawWayType.CURRENCY_CGPAY]: () => new WithdrawalCGPay(),
	[WithdrawWayType.CURRENCY_CGPAY_USDT]: () => new WithdrawalCGPay(),
	[WithdrawWayType.CURRENCY_EBPAY]: () => new WithdrawalEbpay(),
	[WithdrawWayType.CURRENCY_TOPAY]: () => new WithdrawalTopay(),
	[WithdrawWayType.CURRENCY_KOIPAY]: () => new WithdrawalKOIPay(),
	[WithdrawWayType.CURRENCY_DEBIT_CARD]: () => new WithdrawalDebitCard(),
	[WithdrawWayType.CURRENCY_CRYPTO_ERC]: () => new WithdrawalErc20(),
	[WithdrawWayType.CURRENCY_CRYPTO_TRC]: () => new WithdrawalTrc20(),
	[WithdrawWayType.CURRENCY_GOUBAO]: () => new WithdrawalGoubao(),
	[WithdrawWayType.CURRENCY_PAY988]: () => new WithdrawalPay988(),
	[WithdrawWayType.CURRENCY_PAY808]: () => new WithdrawalPay808(),
	[WithdrawWayType.CURRENCY_KDPAY]: () => new WithdrawalKDPay(),
	[WithdrawWayType.CURRENCY_ALIPAY]: () => new WithdrawalAliPay()
};
const WithdrawalMethodFactory = {
	create(withdrawalId) {
		const instance = withdrawals[withdrawalId];
		if (instance === void 0) throw new Error("No WithdrawalMethod instance");
		return instance();
	},
	has(withdrawalId) {
		return withdrawals[withdrawalId] !== void 0;
	}
};
//#endregion
//#region src/cashflow/depositMethods/action/ActionJS.ts
var ActionJS = class {
	constructor(data, argument) {
		this.data = data;
		this.argument = argument;
		_defineProperty(this, "finishedCallback", () => {
			throw new Error("no set closed callback");
		});
	}
};
//#endregion
export { ActionJS, ActionKind, DepositCGPay, DepositCryptoErc, DepositCryptoTrc, DepositECNY, DepositEbpay, DepositGoubao, DepositKDPay, DepositKOIPay, DepositMethodBase, DepositMethodFactory, DepositOnline, DepositOnlineManual, DepositPay808, DepositPay988, DepositScanAlipay, DepositScanWechat, DepositTopay, DepositUnionPayCloud, DepositUnionPayQuick, LOADING_HTML, ONE_DAY_IN_MS, PERMANENT_TIME, THREE_DAYS_IN_MS, THREE_HOURS_IN_MS, TRANSPARENT_IMAGE, WithdrawalAgentTransfer, WithdrawalAliPay, WithdrawalCGPay, WithdrawalDebitCard, WithdrawalEbpay, WithdrawalErc20, WithdrawalGoubao, WithdrawalKDPay, WithdrawalKOIPay, WithdrawalMethodBase, WithdrawalMethodFactory, WithdrawalPay808, WithdrawalPay988, WithdrawalTopay, WithdrawalTrc20, announcementIcon, canvasToImage, copyString, createShouldDisableDate, createShouldDisableDateBetween, createShouldDisableFuture, createTokenRefreshHandler, downloadFile, endOfDayTaipei, endOfMonthTaipei, endOfWeekTaipei, endOfYesterdayTaipei, filterHTMLTags, formatTaipeiTime, getAllowDateRangeOfOneMonths, getAllowDateRangeOfTwoWeeks, getAllowOffsetDaysRange, getAllowedTimeStamps, getBankIcon, getBankImage, getCorrectedDateRange, getDateAtQuery, getDateQuery, getDayArray, getDefaultDateRangeOfToday, getIdleStorage, getLastWeek, getMaxInteger, getMonthArray, getPlayerRankImage, getRangeOfYesterday, getTaipeiAllowDateRangeOfTwoWeeks, getTaipeiAllowOffsetDaysRange, getTaipeiCalendarTimestampRangeOfThisMonthToToday, getTaipeiCalendarTimestampRangeOfToday, getTaipeiCalendarTimestampRangeOfYesterday, getTaipeiDefaultDateRangeOfToday, getTaipeiLastWeek, getTaipeiNowTime, getTaipeiRangeOfYesterday, getTaipeiThisWeek, getTaipeiTime, getThisMonth, getThisWeek, getWebpClass, getWeekArray, goldenCashFormat, htmlToCanvas, isEditableSystemActivitiesHelper, loadScript, normalizeAlphanumeric, normalizeMaxNumber, normalizeMinNumber, normalizeNestedJson, openQQ, openWindow, recursivelyParseJSON, registerIdleLogout, scrollIntoView, scrollMore, setLastMonth, setLastSevenDays, setLastTwoMonths, setLastWeek, setPermanent, setThisMonth, setThisMonthToYesterday, setThisWeek, setToday, setTomorrow, setUpcomingDays, setUpcomingDaysFromNow, setUpcomingHoursFromNow, setViewportOnDesktop, setWindowLoading, setYesterday, splitTextByUrl, startOfDayTaipei, startOfMonthTaipei, startOfWeekTaipei, startOfYearTaipei, startOfYesterdayTaipei, supportWebp, toTimezonedDate };

//# sourceMappingURL=index.mjs.map